class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    if (!this.mainDetailsToggle) return;

    this.content = this.mainDetailsToggle.querySelector('summary')?.nextElementSibling;
    this.closeTimer = null;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 120);
  }

  onToggle() {
    if (!this.content) return;
    if (!this.animations) this.animations = this.content.getAnimations?.() || [];

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play?.());
    } else {
      this.animations.forEach((animation) => animation.cancel?.());
    }
  }

  close() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    if (!this.mainDetailsToggle) return;
    this.mainDetailsToggle.removeAttribute('open');
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');
  }

  disconnectedCallback() {
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }
}

if (!customElements.get('details-disclosure')) {
  customElements.define('details-disclosure', DetailsDisclosure);
}

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    if (!this.mainDetailsToggle) return;

    this.header =
      document.querySelector('.header-wrapper') ||
      document.querySelector('.fp-header-row') ||
      document.querySelector('.app-che-header');

    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.closeSiblings = this.closeSiblings.bind(this);

    this.nestedTimers = new Map();

    this.mainDetailsToggle.addEventListener('mouseenter', this.onEnter);
    this.mainDetailsToggle.addEventListener('mouseleave', this.onLeave);

    // Close other menus before native toggle (toggle event can be async)
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.addEventListener('pointerdown', this.closeSiblings);
      summary.addEventListener('focus', this.closeSiblings);
    }

    // Nested flyouts — hover open/close with the same delay pattern
    this.querySelectorAll('details.fp-desktop-nav__nested').forEach((nested) => {
      nested.addEventListener('mouseenter', () => {
        clearTimeout(this.nestedTimers.get(nested));
        clearTimeout(this.closeTimer);
        nested.setAttribute('open', '');
      });
      nested.addEventListener('mouseleave', () => {
        const nestedTimer = setTimeout(() => nested.removeAttribute('open'), 160);
        this.nestedTimers.set(nested, nestedTimer);
      });
    });

    // Keep parent open while interacting with submenu links
    const submenu = this.mainDetailsToggle.querySelector('.fp-desktop-nav__submenu');
    if (submenu) {
      submenu.addEventListener('mouseenter', () => clearTimeout(this.closeTimer));
    }
  }

  close() {
    if (this.nestedTimers) {
      this.nestedTimers.forEach((timer) => clearTimeout(timer));
      this.nestedTimers.clear();
    }
    this.querySelectorAll('details.fp-desktop-nav__nested[open]').forEach((nested) => {
      nested.removeAttribute('open');
    });
    super.close();
  }

  disconnectedCallback() {
    if (this.nestedTimers) {
      this.nestedTimers.forEach((timer) => clearTimeout(timer));
      this.nestedTimers.clear();
    }
    super.disconnectedCallback();
  }

  closeSiblings() {
    document.querySelectorAll('header-menu > details[open]').forEach((details) => {
      if (details !== this.mainDetailsToggle) {
        details.removeAttribute('open');
        const summary = details.querySelector('summary');
        if (summary) summary.setAttribute('aria-expanded', 'false');
      }
    });
  }

  onEnter() {
    clearTimeout(this.closeTimer);
    this.openMenu();
  }

  onLeave() {
    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), 180);
  }

  openMenu() {
    this.closeSiblings();
    this.mainDetailsToggle.setAttribute('open', '');
    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');
  }

  onToggle() {
    if (!this.mainDetailsToggle) return;
    if (this.mainDetailsToggle.open) {
      this.closeSiblings();
    }

    const summary = this.mainDetailsToggle.querySelector('summary');
    if (summary) {
      summary.setAttribute('aria-expanded', this.mainDetailsToggle.open ? 'true' : 'false');
    }

    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

// Stabilize header search <details> — don't snap shut while using the panel
(() => {
  const bindSearchDetails = (searchDetails) => {
    if (!searchDetails || searchDetails.dataset.fpSearchBound === 'true') return;
    const summary = searchDetails.querySelector('summary');
    if (!summary) return;

    searchDetails.dataset.fpSearchBound = 'true';

    let closeTimer = null;
    const panel = searchDetails.querySelector('.fp-header-search__panel');

    const keepOpen = () => clearTimeout(closeTimer);
    const scheduleClose = (event) => {
      if (searchDetails.contains(event.relatedTarget)) return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        if (!searchDetails.matches(':hover') && !searchDetails.contains(document.activeElement)) {
          searchDetails.removeAttribute('open');
        }
      }, 200);
    };

    searchDetails.addEventListener('mouseenter', keepOpen);
    searchDetails.addEventListener('mouseleave', scheduleClose);
    if (panel) {
      panel.addEventListener('mouseenter', keepOpen);
      panel.addEventListener('focusin', keepOpen);
    }
  };

  const initHeaderSearch = () => {
    document.querySelectorAll('details.fp-header-search').forEach(bindSearchDetails);
  };

  if (!window.__fpHeaderSearchDocBound) {
    window.__fpHeaderSearchDocBound = true;

    document.addEventListener('click', (event) => {
      document.querySelectorAll('details.fp-header-search[open]').forEach((searchDetails) => {
        if (searchDetails.contains(event.target)) return;
        searchDetails.removeAttribute('open');
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('details.fp-header-search[open]').forEach((searchDetails) => {
        searchDetails.removeAttribute('open');
        const summary = searchDetails.querySelector('summary');
        if (summary) summary.focus();
      });
    });

    document.addEventListener('shopify:section:load', initHeaderSearch);
  }

  initHeaderSearch();
})();
